import React, { useState } from 'react';
import { Building2, CheckCircle2, Mail, MessageCircle, Phone, ShieldCheck, Stethoscope, UserRound } from 'lucide-react';
type RegistrationValues = {
  doctorName: string;
  organizationName: string;
  phone: string;
  lineId: string;
  email: string;
  receivePromotions: boolean;
};
type RegistrationErrors = Partial<Record<'doctorName' | 'organizationName' | 'phone' | 'email', string>>;
const initialValues: RegistrationValues = {
  doctorName: '',
  organizationName: '',
  phone: '',
  lineId: '',
  email: '',
  receivePromotions: false
};
export function PractitionerRegistrationForm() {
  const [values, setValues] = useState<RegistrationValues>(initialValues);
  const [errors, setErrors] = useState<RegistrationErrors>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const updateValue = <Key extends keyof RegistrationValues,>(key: Key, value: RegistrationValues[Key]) => {
    setValues(current => ({
      ...current,
      [key]: value
    }));
    if (key in errors) {
      setErrors(current => ({
        ...current,
        [key]: undefined
      }));
    }
  };
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validateRegistration(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) {
      setIsSubmitted(true);
    }
  };
  const handleReset = () => {
    setValues(initialValues);
    setErrors({});
    setIsSubmitted(false);
  };
  if (isSubmitted) {
    return <section className="rounded-3xl border border-brand-lightCopper/50 bg-white p-7 text-center shadow-xl shadow-brand-copper/5 sm:p-10" aria-labelledby="registration-success-title" data-id="element-710">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-green-50 text-green-600" data-id="element-711">
          <CheckCircle2 className="h-9 w-9" aria-hidden="true" data-id="element-712" />
        </div>
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-brand-copper" data-id="element-713">
          ส่งข้อมูลเรียบร้อย
        </p>
        <h2 id="registration-success-title" className="text-2xl font-semibold text-gray-900 sm:text-3xl" data-id="element-714">
          ขอบคุณที่ลงทะเบียนกับ Pola Group
        </h2>
        <p className="mx-auto mt-4 max-w-md text-sm leading-7 text-gray-600" data-id="element-715">
          ทีมงานจะตรวจสอบข้อมูลและติดต่อกลับตามช่องทางที่คุณระบุไว้
        </p>
        <button type="button" onClick={handleReset} className="mt-8 rounded-xl border border-brand-copper px-5 py-3 text-sm font-semibold text-brand-darkCopper transition-colors hover:bg-brand-copper hover:text-white focus:outline-none focus:ring-2 focus:ring-brand-copper focus:ring-offset-2" data-id="element-716">
          ลงทะเบียนเพิ่มเติม
        </button>
      </section>;
  }
  return <section className="overflow-hidden rounded-3xl border border-brand-lightCopper/50 bg-white shadow-xl shadow-brand-copper/5" aria-labelledby="practitioner-registration-title" data-id="element-717">
      <div className="border-b border-brand-lightCopper/30 bg-brand-bg/50 px-6 py-7 sm:px-10 sm:py-8" data-id="element-718">
        <div className="flex items-start gap-4" data-id="element-719">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-brand-copper text-white shadow-lg shadow-brand-copper/20" data-id="element-720">
            <Stethoscope className="h-6 w-6" aria-hidden="true" data-id="element-721" />
          </div>
          <div data-id="element-722">
            <p className="mb-1 text-xs font-bold uppercase tracking-[0.16em] text-brand-copper" data-id="element-723">
              Pola Group
            </p>
            <h1 id="practitioner-registration-title" className="text-2xl font-semibold text-gray-900 sm:text-3xl" data-id="element-724">
              ลงทะเบียนสำหรับแพทย์
            </h1>
            <p className="mt-2 text-sm leading-6 text-gray-600" data-id="element-725">
              สำหรับแพทย์ โรงพยาบาล และคลินิกที่สนใจผลิตภัณฑ์ Singderm
            </p>
          </div>
        </div>
      </div>

      <form noValidate onSubmit={handleSubmit} className="space-y-5 px-6 py-7 sm:px-10 sm:py-8" data-id="element-726">
        <p className="text-sm text-gray-500" data-id="element-727">
          กรุณากรอกข้อมูลเพื่อให้ทีมงานติดต่อกลับ{' '}
          <span className="text-red-500" data-id="element-728">*</span> คือข้อมูลที่จำเป็น
        </p>

        <FormField id="doctor-name" label="ชื่อแพทย์" required error={errors.doctorName} icon={<UserRound className="h-5 w-5" aria-hidden="true" data-id="element-730" />} data-id="element-729">
          <input id="doctor-name" name="doctorName" type="text" autoComplete="name" value={values.doctorName} onChange={event => updateValue('doctorName', event.target.value)} aria-invalid={Boolean(errors.doctorName)} aria-describedby={errors.doctorName ? 'doctor-name-error' : undefined} className={inputClassName(Boolean(errors.doctorName))} placeholder="เช่น นพ. สมชาย ใจดี" data-id="element-731" />
        </FormField>

        <FormField id="organization-name" label="ชื่อโรงพยาบาล / คลินิก" required error={errors.organizationName} icon={<Building2 className="h-5 w-5" aria-hidden="true" data-id="element-733" />} data-id="element-732">
          <input id="organization-name" name="organizationName" type="text" autoComplete="organization" value={values.organizationName} onChange={event => updateValue('organizationName', event.target.value)} aria-invalid={Boolean(errors.organizationName)} aria-describedby={errors.organizationName ? 'organization-name-error' : undefined} className={inputClassName(Boolean(errors.organizationName))} placeholder="ระบุชื่อโรงพยาบาลหรือคลินิก" data-id="element-734" />
        </FormField>

        <div className="grid gap-5 sm:grid-cols-2" data-id="element-735">
          <FormField id="phone" label="เบอร์โทรศัพท์" required error={errors.phone} icon={<Phone className="h-5 w-5" aria-hidden="true" data-id="element-737" />} data-id="element-736">
            <input id="phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" value={values.phone} onChange={event => updateValue('phone', event.target.value)} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'phone-error' : undefined} className={inputClassName(Boolean(errors.phone))} placeholder="08X-XXX-XXXX" data-id="element-738" />
          </FormField>

          <FormField id="line-id" label="LINE ID" icon={<MessageCircle className="h-5 w-5" aria-hidden="true" data-id="element-740" />} data-id="element-739">
            <input id="line-id" name="lineId" type="text" autoComplete="off" value={values.lineId} onChange={event => updateValue('lineId', event.target.value)} className={inputClassName(false)} placeholder="ระบุ LINE ID" data-id="element-741" />
          </FormField>
        </div>

        <FormField id="email" label="อีเมล" required error={errors.email} icon={<Mail className="h-5 w-5" aria-hidden="true" data-id="element-743" />} data-id="element-742">
          <input id="email" name="email" type="email" inputMode="email" autoComplete="email" value={values.email} onChange={event => updateValue('email', event.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} className={inputClassName(Boolean(errors.email))} placeholder="name@clinic.com" data-id="element-744" />
        </FormField>

        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5 transition-colors hover:border-brand-lightCopper" data-id="element-745">
          <input type="checkbox" checked={values.receivePromotions} onChange={event => updateValue('receivePromotions', event.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-copper focus:ring-brand-copper" data-id="element-746" />
          <span className="text-sm font-medium text-gray-700" data-id="element-747">
            สนใจรับโปรโมชั่น
            <span className="mt-0.5 block text-xs font-normal text-gray-500" data-id="element-748">
              รับข่าวสาร กิจกรรม และข้อเสนอพิเศษจาก Singderm Thailand
            </span>
          </span>
        </label>

        <div className="flex items-start gap-2.5 rounded-xl bg-brand-bg/60 px-4 py-3 text-xs leading-5 text-gray-600" data-id="element-749">
          <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-copper" aria-hidden="true" data-id="element-750" />
          <p data-id="element-751">
            ข้อมูลของคุณจะถูกเก็บเป็นความลับและใช้เพื่อการติดต่อเกี่ยวกับผลิตภัณฑ์
            บริการ และการดูแลโดยทีมงาน Pola Group เท่านั้น
          </p>
        </div>

        <button type="submit" className="w-full rounded-xl bg-brand-copper px-5 py-3.5 text-sm font-semibold text-white shadow-lg shadow-brand-copper/20 transition-colors hover:bg-brand-darkCopper focus:outline-none focus:ring-2 focus:ring-brand-copper focus:ring-offset-2" data-id="element-752">
          ส่งข้อมูลลงทะเบียน
        </button>
      </form>
    </section>;
}
type FormFieldProps = {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  icon: React.ReactNode;
  children: React.ReactNode;
};
function FormField({
  id,
  label,
  required = false,
  error,
  icon,
  children
}: FormFieldProps) {
  return <div data-id="element-753">
      <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-gray-700" data-id="element-754">
        {label} {required && <span className="text-red-500" data-id="element-755">*</span>}
      </label>
      <div className="relative" data-id="element-756">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-757">
          {icon}
        </span>
        {children}
      </div>
      {error && <p id={`${id}-error`} className="mt-1.5 text-xs text-red-600" role="alert" data-id="element-758">
          {error}
        </p>}
    </div>;
}
function inputClassName(hasError: boolean) {
  return `w-full rounded-xl border bg-white py-3 pl-10 pr-4 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:ring-2 focus:ring-brand-copper/20 ${hasError ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-brand-copper'}`;
}
function validateRegistration(values: RegistrationValues): RegistrationErrors {
  const errors: RegistrationErrors = {};
  const phoneDigits = values.phone.replace(/\D/g, '');
  if (!values.doctorName.trim()) {
    errors.doctorName = 'กรุณาระบุชื่อแพทย์';
  }
  if (!values.organizationName.trim()) {
    errors.organizationName = 'กรุณาระบุชื่อโรงพยาบาลหรือคลินิก';
  }
  if (phoneDigits.length < 8 || phoneDigits.length > 15) {
    errors.phone = 'กรุณาระบุเบอร์โทรศัพท์ที่ถูกต้อง';
  }
  if (!values.email.trim()) {
    errors.email = 'กรุณาระบุอีเมล';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errors.email = 'กรุณาระบุอีเมลในรูปแบบที่ถูกต้อง';
  }
  return errors;
}